import makeConcurrent from 'make-concurrent';
import { url } from '@exodus/fetch/url';
import { conversionByRate } from '@exodus/currency';
import { SwapMode } from '../../common/types/index.js';
import { BASE_URL, DEFAULT_SLIPPAGE } from './constants.js';
import checkOrderIsValidWithProvider from './verifiers/index.js';
import { getUserTimezone, pidToTxData } from './utils.js';
import { deconstructPairId } from '../../utils.js';
import { isAssetPairExchangeable, parseFetchivalError, parseFetchivalErrorWithOrder, } from '../utils.js';
import { CreateOrderError, CreateTxError, ExchangeError, ExchangeOrderError, GetProviderOrderError, MissingFromAssetError, MissingPairError, NoQuoteAvailableError, SendTxError, } from '../../errors.js';
import { LOGS } from '../../constants.js';
const RATE_EXPIRY_OFFSET = 2000;
class Exodus {
    name = 'exodus';
    source = 'exodus';
    _appName;
    _appPlatform;
    _appVersion;
    _assetsService;
    _baseUrl;
    _fetchival;
    _options;
    _tracer;
    _cachedRates = new Map();
    constructor({ assetsService, fetchival, appName, appVersion, appPlatform, baseUrl, options = Object.create(null), tracer, }) {
        this._baseUrl = new URL(baseUrl || BASE_URL);
        this._appName = appName;
        this._appPlatform = appPlatform;
        this._appVersion = appVersion;
        this._assetsService = assetsService;
        this._options = options;
        this._tracer = tracer;
        this._fetchival = fetchival;
    }
    async isPairSupported(pairId) {
        const { from, to } = deconstructPairId(pairId);
        const assetSources = await this._assetsService.getAssetSources([this.source], [from, to]);
        return isAssetPairExchangeable({ from, to, assetSources });
    }
    async getQuote(params) {
        const { pairId, amount, swapMode = SwapMode.ExactIn } = params;
        const { from, to } = deconstructPairId(pairId);
        const [rates, fromAsset, toAsset] = await Promise.all([
            this._getRates(pairId),
            this._assetsService.getOneById(from),
            this._assetsService.getOneById(to),
        ]);
        if (!fromAsset || !toAsset) {
            throw new MissingPairError({ pairId, fromAsset, toAsset });
        }
        const bestQuote = rates.reduce((acc, rate) => {
            const fromUnit = fromAsset.currency;
            const toUnit = toAsset.currency;
            let rateOutputAmount;
            if (swapMode === SwapMode.ExactIn) {
                const convert = conversionByRate(fromUnit, toUnit, rate.amount.value);
                rateOutputAmount = convert(amount).sub(rate.minerFee.value);
            }
            else {
                const convert = conversionByRate(toUnit, fromUnit, 1 / parseFloat(rate.amount.value));
                rateOutputAmount = convert(amount.add(rate.minerFee.value));
            }
            const isNewRateUsable = swapMode === SwapMode.ExactIn
                ? rate.min.value.lte(amount) && rate.max.value.gte(amount)
                : rate.min.value.lte(rateOutputAmount) && rate.max.value.gte(rateOutputAmount);
            if (!isNewRateUsable) {
                return acc;
            }
            if (!acc) {
                return { amount: rateOutputAmount, minerFee: rate.minerFee.value, rate };
            }
            const isNewQuoteBetter = swapMode === SwapMode.ExactIn
                ? rateOutputAmount.gt(acc.amount)
                : rateOutputAmount.lt(acc.amount);
            return isNewQuoteBetter
                ? { amount: rateOutputAmount, minerFee: rate.minerFee.value, rate }
                : acc;
        }, null);
        if (!bestQuote) {
            const { value: minValue = 'UNDEF_VAL', assetId: minAssetId = 'UNDEF_ASSET' } = rates[0]?.min || {};
            const { value: maxValue = 'UNDEF_VAL', assetId: maxAssetId = 'UNDEF_ASSET' } = rates[0]?.max || {};
            throw new NoQuoteAvailableError({
                maxAssetId,
                maxValue: maxValue === 'UNDEF_VAL' ? 'UNDEF_VAL' : maxValue.toDefaultString(),
                minAssetId,
                minValue: minValue === 'UNDEF_VAL' ? 'UNDEF_VAL' : minValue.toDefaultString(),
            });
        }
        let pidBytes;
        if (bestQuote.rate.features?.includes('pid')) {
            const randomPid = '843d630a3a7a4846615c4f6492d7135789575f3487f9b0e37dea794ffc373795';
            pidBytes = pidToTxData(randomPid);
        }
        const rate = bestQuote.rate.amount.value;
        const outputAmount = swapMode === SwapMode.ExactIn
            ? toAsset.currency.defaultUnit(bestQuote.amount.toDefaultString({ unit: false }))
            : bestQuote.amount;
        return {
            amount: outputAmount,
            providerName: this.name,
            rateId: bestQuote.rate.id,
            rate,
            exchangeFeeData: {
                feeOpts: { pidBytes },
            },
            exchangeTxData: {},
        };
    }
    async prepareExchange({ fromAddress, fromAddressTag, fromAmount, pairId, slippage = DEFAULT_SLIPPAGE, toAddress, toAddressTag, toAmount, isSendAll, anonymousId, referralCode, swapMode = SwapMode.ExactIn, quote, createTx, onOrderIdGenerated, }) {
        try {
            this._traceMessage(LOGS.CREATING_ORDER);
            this._tracer?.traceTime('CreateCexOrder');
            const orderPayload = {
                fromAddress,
                fromAddressTag,
                fromAmount: fromAmount.toDefaultString({ unit: false }),
                pairId,
                slippage,
                toAddress,
                toAddressTag,
                toAmount: toAmount.toDefaultString({ unit: false }),
                referralCode,
                swapMode,
            };
            const order = await this._request(new URL(`${this._baseUrl}/orders?${new URLSearchParams({
                extraFeatures: [...(this._options.extraFeatures || []), 'stringAmounts'].join(','),
            }).toString()}`), { anonymousId, userTimezone: getUserTimezone() })
                .post(orderPayload)
                .catch(async (err) => {
                const { message, orderId } = await parseFetchivalErrorWithOrder(err);
                this._traceMessage(LOGS.FAILED_TO_CREATE_ORDER);
                throw new CreateOrderError({
                    message,
                    details: orderPayload,
                    orderId,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('CreateCexOrder'));
            if (typeof onOrderIdGenerated === 'function') {
                onOrderIdGenerated(order.id);
            }
            this._traceMessage(LOGS.GETTING_PROVIDER_ORDER);
            this._tracer?.traceTime('GetProviderOrder');
            const providerOrder = await this._request(url `${this._baseUrl}/orders/${order.id}/provider-order`)
                .get()
                .catch(async (err) => {
                this._traceMessage(LOGS.FAILED_TO_GET_TX);
                const errorMessage = await parseFetchivalError(err);
                throw new GetProviderOrderError({ message: errorMessage, orderId: order.id });
            })
                .finally(() => this._tracer?.traceTimeEnd('GetProviderOrder'));
            if (!providerOrder) {
                throw new ExchangeOrderError({
                    message: `Unable to get order ${order.id} from provider`,
                    orderId: order.id,
                });
            }
            if (!this._options.bypassProvidersValidation) {
                this._traceMessage(LOGS.CHECKING_ORDER_VALIDITY);
                this._tracer?.traceTime('CheckOrderIsValid');
                await checkOrderIsValidWithProvider({
                    order,
                    providerOrder,
                    fetchival: this._fetchival,
                })
                    .catch((err) => {
                    this._traceMessage(LOGS.FAILED_TO_VALIDATE_ORDER);
                    throw err;
                })
                    .finally(() => this._tracer?.traceTimeEnd('CheckOrderIsValid'));
            }
            const { from } = deconstructPairId(pairId);
            this._tracer?.traceTime('GetExchangeOpts');
            const [fromAsset, opts] = await Promise.all([
                this._assetsService.getOneById(from),
                this._getExchangeOpts(order).finally(() => this._tracer?.traceTimeEnd('GetExchangeOpts')),
            ]);
            if (!fromAsset) {
                throw new MissingFromAssetError({ fromAsset: from, orderId: order.id });
            }
            this._traceMessage(LOGS.CREATING_TX);
            this._tracer?.traceTime('CreateTx');
            const createTxPayload = {
                assetId: order.amount.assetId,
                amount: fromAsset.currency.defaultUnit(order.amount.value),
                recipient: order.payInAddress,
                opts,
                isSendAll,
                feeOpts: quote.exchangeFeeData.feeOpts,
            };
            const transaction = await createTx(createTxPayload)
                .catch((err) => {
                this._traceMessage(LOGS.FAILED_TO_CREATE_TX);
                throw new CreateTxError({
                    message: err.message,
                    details: createTxPayload,
                    orderId: order.id,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('CreateTx'));
            return {
                transaction,
                orderId: order.id,
                fromAmount,
            };
        }
        catch (err) {
            if (err instanceof ExchangeError) {
                throw err;
            }
            throw new ExchangeError({ message: err.message }, { cause: err });
        }
    }
    async executeExchange({ orderId, transaction, sendTx, }) {
        try {
            this._traceMessage(LOGS.SENDING_TX);
            this._tracer?.traceTime('SendTx');
            const { txId } = await sendTx(transaction)
                .catch((err) => {
                this._traceMessage(LOGS.FAILED_TO_SEND_TX);
                throw new SendTxError({
                    message: err.message,
                    details: transaction,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('SendTx'));
            this._traceMessage(LOGS.GETTING_PROVIDER_ORDER);
            this._tracer?.traceTime('GetProviderOrder');
            const { provider, id: providerOrderId } = await this._request(url `${this._baseUrl}/orders/${orderId}/provider-order`)
                .get()
                .catch(async (err) => {
                this._traceMessage(LOGS.FAILED_TO_GET_TX);
                const errorMessage = await parseFetchivalError(err);
                throw new GetProviderOrderError({ message: errorMessage, orderId });
            })
                .finally(() => this._tracer?.traceTimeEnd('GetProviderOrder'));
            this._traceMessage(LOGS.UPDATING_ORDER);
            this._request(url `${this._baseUrl}/orders/${orderId}`)
                .patch({ transactionId: txId })
                .catch((err) => console.warn(`Fail to update order ${orderId} with txId ${txId}`, err));
            return {
                transactions: [{ type: 'depositTransaction', id: txId }],
                svc: provider.slug,
                providerOrderId,
            };
        }
        catch (err) {
            if (err instanceof ExchangeError) {
                throw err;
            }
            throw new ExchangeError({ message: err.message }, { cause: err });
        }
    }
    getLimits = makeConcurrent(async ({ pairId }) => {
        const rates = (await this._getRates(pairId));
        const { from, to } = deconstructPairId(pairId);
        const [fromAsset, toAsset] = await Promise.all([
            this._assetsService.getOneById(from),
            this._assetsService.getOneById(to),
        ]);
        if (!fromAsset || !toAsset) {
            throw new MissingPairError({ pairId, fromAsset, toAsset });
        }
        return rates.map(({ amount, min, max, minerFee }) => {
            const convert = conversionByRate(toAsset.currency, fromAsset.currency, 1 / parseFloat(amount.value));
            return {
                [SwapMode.ExactIn]: {
                    min: min.value,
                    max: max.value,
                },
                [SwapMode.ExactOut]: {
                    min: convert(min.value).add(minerFee.value),
                    max: convert(max.value).add(minerFee.value),
                },
            };
        });
    });
    setGeolocation(_geolocation) {
    }
    async getPairs(params) {
        return this._request(url `${this._baseUrl}/pairs`).get(params);
    }
    _getRates = makeConcurrent(async (pairId) => {
        const cachedRates = this._cachedRates.get(pairId);
        const isCachedInvalid = !cachedRates || cachedRates.some((rate) => rate.expiry + RATE_EXPIRY_OFFSET <= Date.now());
        if (isCachedInvalid) {
            const rates = await this._request(url `${this._baseUrl}/pairs/${pairId}/rates`).get({
                ...(this._options.extraFeatures ? { extraFeatures: this._options.extraFeatures } : {}),
            });
            const { from, to } = deconstructPairId(pairId);
            const [fromAsset, toAsset] = await Promise.all([
                this._assetsService.getOneById(from),
                this._assetsService.getOneById(to),
            ]);
            if (!fromAsset || !toAsset) {
                throw new MissingPairError({ pairId, fromAsset, toAsset });
            }
            if (rates.length > 0) {
                this._cachedRates.set(pairId, rates.map((rate) => {
                    const min = fromAsset.currency.defaultUnit(rate.min.value);
                    const max = fromAsset.currency.defaultUnit(rate.max.value);
                    const minerFee = toAsset.currency.defaultUnit(rate.minerFee.value);
                    return {
                        ...rate,
                        amount: {
                            ...rate.amount,
                            value: rate.amount.value.toString(),
                        },
                        min: {
                            ...rate.min,
                            value: min,
                        },
                        max: {
                            ...rate.max,
                            value: max,
                        },
                        minerFee: {
                            ...rate.minerFee,
                            value: minerFee,
                        },
                    };
                }));
            }
        }
        return this._cachedRates.get(pairId) || [];
    });
    _request(url, { anonymousId, userTimezone } = {}) {
        return this._fetchival(url, {
            headers: {
                'App-Name': this._appName,
                'App-Version': this._appVersion,
                ...(this._appPlatform ? { 'App-Platform': this._appPlatform } : {}),
                ...(anonymousId ? { anonymousId } : {}),
                ...(userTimezone ? { 'User-Timezone': userTimezone } : {}),
            },
        });
    }
    _getExchangeOpts = async (order) => {
        const opts = {};
        if (order.extraFeatures?.pid) {
            opts.pidBytes = pidToTxData(order.extraFeatures.pid);
        }
        if (order.payInAddressTag) {
            opts.payInAddressTag = order.payInAddressTag;
        }
        return opts;
    };
    _traceMessage(message) {
        this._tracer?.traceMessage(message);
    }
}
export default Exodus;
